import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FaArrowLeft, FaLink, FaSpinner, FaExternalLinkAlt } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import { ToastTypes, useMessage } from '../../../base/context/MessageProvider';
import { fetchToken } from '../../../lib/globalApiClient';
import './findSimilar.css';

const BACKEND_API = process.env.REACT_APP_APIUrl || 'https://api.importerr.com/api/';

// Use onePiecePrice (one-piece) as primary RMB source for all UI pricing.
const getApRmbPrice = (sku, fallback = 0) => {
  const ap = parseFloat(
    sku?.fenxiaoPriceInfo?.onePiecePrice ??
    sku?.onePiecePrice ??
    sku?.priceInfo?.onePiecePrice ??
    sku?.priceInfo?.price ??
    sku?.price ??
    sku?.salePrice ??
    sku?.retailPrice ??
    sku?.ap ??
    0
  );
  if (Number.isFinite(ap) && ap > 0) return ap;

  const fallbackPrice = parseFloat(fallback || 0);
  return Number.isFinite(fallbackPrice) && fallbackPrice > 0 ? fallbackPrice : 0;
};

const SellerFindSimilar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const message = useMessage();

  const [sortBy, setSortBy] = useState('relevance');
  const [priceRange, setPriceRange] = useState('all');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [amazonProduct, setAmazonProduct] = useState(null);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreProducts, setHasMoreProducts] = useState(false);
  const [mappingProductId, setMappingProductId] = useState(null);
  const [pricingCache, setPricingCache] = useState({}); // Cache for calculated prices

  // Get ASIN and image from query params or state
  const searchParams = new URLSearchParams(location.search);
  const asinFromQuery = searchParams.get('asin') || '';
  const imageFromQuery = searchParams.get('image') || '';
  const titleFromQuery = searchParams.get('title') || '';
  const priceFromQuery = searchParams.get('price') || '';
  const amazonProductFromState = location.state?.amazonProduct;

  // Calculate INR price using the same API as website (product/pricing-algorithms/calculate)
  // Uses same calculation data format as frontend/src/website/views/products/index.js
  const calculateINRPrice = useCallback(async (rmbPrice, offerId = '') => {
    if (!rmbPrice || rmbPrice <= 0) return 0;

    // Check cache first
    const cacheKey = rmbPrice.toFixed(2);
    if (pricingCache[cacheKey]) {
      return pricingCache[cacheKey];
    }

    try {
      // Use same calculation data format as website search list
      const calculationData = {
        ap: parseFloat(rmbPrice),           // Alibaba Price (RMB)
        ls: 0,                               // Local Shipping (will use default from algo)
        hsn: false,                          // HSN tax flag
        hsnTaxPercent: 18,                   // Default GST
        dw: 0.5,                             // Default dead weight (kg)
        vw: 0.5 / 5000,                      // Default volumetric weight
        sku: String(offerId || ''),          // Product SKU/offerId
      };

      console.log('[FindSimilar] Calculating price for RMB:', rmbPrice, 'offerId:', offerId);
      console.log('[FindSimilar] Calculation data:', calculationData);

      const response = await fetch(`${BACKEND_API}product/pricing-algorithms/calculate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(calculationData)
      });

      if (!response.ok) {
        console.error('[FindSimilar] API Error:', response.status, response.statusText);
        return 0;
      }

      const data = await response.json();
      console.log('[FindSimilar] API Response:', JSON.stringify(data, null, 2));

      // Use FBP (Final Buyer Price) from breakdown - same as website
      // Response structure: { status: {...}, data: { breakdown: { FBP: ... } } }
      if (data?.data?.breakdown?.FBP) {
        const inrPrice = parseFloat(data.data.breakdown.FBP);
        console.log('[FindSimilar] FBP price:', inrPrice);
        // Cache the result
        setPricingCache(prev => ({ ...prev, [cacheKey]: inrPrice }));
        return inrPrice;
      }

      // Alternative response structure (direct breakdown)
      if (data?.breakdown?.FBP) {
        const inrPrice = parseFloat(data.breakdown.FBP);
        console.log('[FindSimilar] FBP price (alt):', inrPrice);
        setPricingCache(prev => ({ ...prev, [cacheKey]: inrPrice }));
        return inrPrice;
      }

      // Fallback to finalPrice if FBP not available
      if (data?.data?.finalPrice) {
        const inrPrice = parseFloat(data.data.finalPrice);
        setPricingCache(prev => ({ ...prev, [cacheKey]: inrPrice }));
        return inrPrice;
      }

      console.warn('[FindSimilar] No FBP found in response');
      return 0;
    } catch (error) {
      console.error('Pricing calculation failed:', error);
      return 0;
    }
  }, [pricingCache]);

  // Helper: Fetch full product details and extract minimum variant price
  const fetchProductDetailsWithVariants = useCallback(async (searchResultItem) => {
    try {
      const baseUrl = process.env.REACT_APP_APIUrl1688;
      const token = await fetchToken();
      const offerId = searchResultItem.offerId || searchResultItem.id || '';

      if (!offerId) {
        return {
          id: `p-${Math.random()}`,
          offerId: '',
          title: searchResultItem.subjectTrans || searchResultItem.subject || searchResultItem.title || 'Product',
          image: searchResultItem.imageUrl || searchResultItem.image || '',
          price: {
            rmb: getApRmbPrice(searchResultItem, 0),
            inr: 0
          },
          variantCount: 1,
          supplier: searchResultItem.companyName || searchResultItem.supplier || 'Supplier',
          monthSold: searchResultItem.monthSold || 0,
          similarity: 0,
        };
      }

      const response = await fetch(baseUrl + '/product-detail-by-offerid', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Token': token
        },
        body: JSON.stringify({ offerId: offerId.toString(), country: 'en' })
      });

      if (!response.ok) {
        console.warn('Failed to fetch product details for', offerId);
        return {
          id: offerId || `p-${Math.random()}`,
          offerId,
          title: searchResultItem.subjectTrans || searchResultItem.subject || searchResultItem.title || 'Product',
          image: searchResultItem.imageUrl || searchResultItem.image || '',
          price: {
            rmb: getApRmbPrice(searchResultItem, 0),
            inr: 0
          },
          variantCount: 1,
          supplier: searchResultItem.companyName || searchResultItem.supplier || 'Supplier',
          monthSold: searchResultItem.monthSold || 0,
          similarity: 0,
        };
      }

      const data = await response.json();
      const productData = data.result?.result || data.product || data.data || data;

      // Extract all variants from possible sources
      let skuInfos = [];
      if (Array.isArray(productData.productSkuInfos) && productData.productSkuInfos.length > 0) {
        skuInfos = productData.productSkuInfos;
      } else if (Array.isArray(productData.skuInfos) && productData.skuInfos.length > 0) {
        skuInfos = productData.skuInfos;
      } else if (Array.isArray(productData.skuList) && productData.skuList.length > 0) {
        skuInfos = productData.skuList;
      } else if (Array.isArray(productData.skus) && productData.skus.length > 0) {
        skuInfos = productData.skus;
      } else if (Array.isArray(productData.productInfo?.skuInfos) && productData.productInfo.skuInfos.length > 0) {
        skuInfos = productData.productInfo.skuInfos;
      } else if (Array.isArray(productData.data?.productSkuInfos) && productData.data.productSkuInfos.length > 0) {
        skuInfos = productData.data.productSkuInfos;
      } else if (Array.isArray(productData.variants) && productData.variants.length > 0) {
        skuInfos = productData.variants;
      }

      // Find minimum RMB price among all variants
      let minRmbPrice = getApRmbPrice(searchResultItem, 0);
      if (skuInfos.length > 0) {
        const prices = skuInfos.map(sku =>
          getApRmbPrice(sku, 0)
        ).filter(p => p > 0);

        if (prices.length > 0) {
          minRmbPrice = Math.min(...prices);
        }
      }

      // Calculate INR price for minimum RMB price
      const inrPrice = await calculateINRPrice(minRmbPrice, offerId);

      return {
        id: offerId || `p-${Math.random()}`,
        offerId,
        title: searchResultItem.subjectTrans || searchResultItem.subject || searchResultItem.title || 'Product',
        image: searchResultItem.imageUrl || searchResultItem.image || '',
        price: {
          rmb: minRmbPrice,
          inr: inrPrice
        },
        variantCount: skuInfos.length || 1,
        supplier: searchResultItem.companyName || searchResultItem.supplier || 'Supplier',
        monthSold: searchResultItem.monthSold || 0,
        similarity: Math.max(95 - (Math.random() * 30), 70),
      };
    } catch (err) {
      console.error('Error fetching product details:', err);
      return {
        id: searchResultItem.offerId || searchResultItem.id || `p-${Math.random()}`,
        offerId: searchResultItem.offerId || searchResultItem.id || '',
        title: searchResultItem.subjectTrans || searchResultItem.subject || searchResultItem.title || 'Product',
        image: searchResultItem.imageUrl || searchResultItem.image || '',
        price: {
          rmb: getApRmbPrice(searchResultItem, 0),
          inr: 0
        },
        variantCount: 1,
        supplier: searchResultItem.companyName || searchResultItem.supplier || 'Supplier',
        monthSold: searchResultItem.monthSold || 0,
        similarity: 0,
      };
    }
  }, [calculateINRPrice]);

  // Compress image for API
  const compressImage = useCallback(async (blob) => {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(blob);

      img.onload = () => {
        URL.revokeObjectURL(url);
        let { width, height } = img;
        const maxDim = 400;
        if (width > maxDim || height > maxDim) {
          const scale = maxDim / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((compressedBlob) => {
          resolve(compressedBlob || blob);
        }, 'image/jpeg', 0.7);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(blob);
      };
      img.src = url;
    });
  }, []);

  // Find similar products using image
  const findSimilarProducts = async (imageUrl, page = 1, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      // Fetch image via proxy
      const proxyUrl = `${BACKEND_API.replace(/\/+$/, '')}/proxy/image?url=${encodeURIComponent(imageUrl)}`;
      const imgResponse = await fetch(proxyUrl);

      if (!imgResponse.ok) {
        throw new Error('Failed to fetch image');
      }

      let imgBlob = await imgResponse.blob();
      imgBlob = await compressImage(imgBlob);

      // Convert to base64
      const base64Image = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(imgBlob);
      });

      // Call similar search API
      const baseUrl = process.env.REACT_APP_APIUrl1688;
      const token = await fetchToken();
      const response = await fetch(baseUrl + '/get-product-info-by-image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Token': token
        },
        body: JSON.stringify({
          imageBase64: base64Image,
          beginPage: page,
          pageSize: 20,
          country: 'en',
        }),
      });

      if (!response.ok) {
        throw new Error('API error');
      }

      const data = await response.json();

      // Handle the API response structure
      let productsData = null;
      if (data.status === 'success' && data.products) {
        productsData = data.products;
      } else if (data.result?.success && data.result?.result?.data) {
        productsData = data.result.result.data;
      } else if (data.products) {
        productsData = data.products;
      } else if (data.data) {
        productsData = data.data;
      } else if (Array.isArray(data)) {
        productsData = data;
      }

      if (productsData && Array.isArray(productsData) && productsData.length > 0) {
        // Fetch full product details for each product to get minimum variant prices
        const productsWithPrices = await Promise.all(
          productsData.map((item) => fetchProductDetailsWithVariants(item))
        );

        // Update pagination info
        if (data.pagination) {
          setHasMoreProducts(page < (data.pagination.totalPage || 1));
        }

        if (append) {
          setSimilarProducts(prev => [...prev, ...productsWithPrices]);
        } else {
          setSimilarProducts(productsWithPrices);
        }
      } else {
        if (!append) {
          setSimilarProducts([]);
        }
      }
    } catch (error) {
      console.error('Find similar failed:', error);
      message.showToast('Failed to find similar products', ToastTypes.ERROR);
      if (!append) {
        setSimilarProducts([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };


  // Map product to ASIN - update offerId in product collection
  const handleMapProduct = async (product, skuId = null) => {
    const asin = amazonProduct?.asin || asinFromQuery;
    if (!asin || !product.offerId) {
      message.showToast('Missing ASIN or Product ID', ToastTypes.ERROR);
      return;
    }

    const mapKey = skuId ? `${product.id}-${skuId}` : product.id;
    setMappingProductId(mapKey);

    try {
      const token = localStorage.getItem('LOGIN_HEADER');
      if (!token) {
        message.showToast('Please login to map products', ToastTypes.ERROR);
        return;
      }

      const response = await fetch(`${BACKEND_API}order/map-asin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          asin: asin,
          offerId: product.offerId.toString(),
          skuId: skuId || null,
          productTitle: product.title,
          productImage: product.image,
          productPrice: product.price.rmb,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const mappedTo = skuId ? `SKU ${skuId}` : `product ${product.offerId}`;
        message.showToast(`Successfully mapped ASIN ${asin} to ${mappedTo}`, ToastTypes.SUCCESS);
        setTimeout(() => {
          navigate(-1);
        }, 1500);
      } else {
        message.showToast(data.message || 'Failed to map product', ToastTypes.ERROR);
      }
    } catch (error) {
      console.error('Map product failed:', error);
      message.showToast('Failed to map product. Please try again.', ToastTypes.ERROR);
    } finally {
      setMappingProductId(null);
    }
  };

  // Open product detail in seller panel
  const handleOpenProduct = (product) => {
    navigate(`/seller/product-detail?offerId=${product.offerId}&asin=${amazonProduct?.asin || asinFromQuery}`, {
      state: { product, amazonProduct }
    });
  };

  // Load more products
  const loadMoreProducts = async () => {
    if (!amazonProduct?.image || loadingMore || !hasMoreProducts) return;
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    await findSimilarProducts(amazonProduct.image, nextPage, true);
  };

  // Initialize page
  useEffect(() => {
    const initializePage = async () => {
      let product = amazonProductFromState;

      if (!product && (imageFromQuery || asinFromQuery)) {
        product = {
          asin: asinFromQuery,
          image: imageFromQuery ? decodeURIComponent(imageFromQuery) : null,
          title: titleFromQuery ? decodeURIComponent(titleFromQuery) : `Amazon Product - ${asinFromQuery}`,
          price: priceFromQuery || '',
        };
      }

      if (product) {
        setAmazonProduct(product);
        setCurrentPage(1);
        setHasMoreProducts(false);

        if (product.image) {
          await findSimilarProducts(product.image, 1, false);
        }
      }
    };

    initializePage();
  }, [amazonProductFromState, imageFromQuery, asinFromQuery]);

  // Filter and sort products
  const filteredAndSortedProducts = useMemo(() => {
    let filtered = [...similarProducts];

    if (priceRange !== 'all') {
      const [min, max] = priceRange === '200+' ? [200, Infinity] : priceRange.split('-').map(Number);
      filtered = filtered.filter(product => {
        const price = product.price.rmb;
        return price >= min && (max === Infinity || price <= max);
      });
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'price-low':
          return a.price.rmb - b.price.rmb;
        case 'price-high':
          return b.price.rmb - a.price.rmb;
        case 'similarity':
          return b.similarity - a.similarity;
        case 'relevance':
        default:
          return 0;
      }
    });

    return filtered;
  }, [similarProducts, sortBy, priceRange]);

  // Image error handler
  const handleImageError = (e) => {
    e.target.style.display = 'none';
    e.target.parentElement.innerHTML = '<div class="sfs-no-image"><span>No Image</span></div>';
  };

  if (!amazonProduct) {
    return (
      <div className="sfs-container">
        <div className="sfs-loading">
          <FaSpinner className="sfs-spinner" />
          <p>Loading product details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sfs-container">
      {/* Header */}
      <div className="sfs-header">
        <button className="sfs-back-btn" onClick={() => navigate(-1)}>
          <FaArrowLeft /> Back
        </button>
        <h2 className="sfs-title">Find Similar Products & Map to ASIN</h2>
      </div>

      {/* Amazon Product Reference */}
      <div className="sfs-amazon-card">
        <div className="sfs-amazon-badge">Amazon Product</div>
        <div className="sfs-amazon-content">
          {amazonProduct.image && (
            <img
              src={amazonProduct.image}
              alt={amazonProduct.title}
              className="sfs-amazon-img"
              crossOrigin="anonymous"
              onError={handleImageError}
            />
          )}
          <div className="sfs-amazon-info">
            <h3 className="sfs-amazon-title">{amazonProduct.title}</h3>
            <div className="sfs-amazon-meta">
              <span className="sfs-asin">ASIN: {amazonProduct.asin || asinFromQuery}</span>
              {amazonProduct.price && <span className="sfs-amazon-price">{amazonProduct.price}</span>}
            </div>
            <p className="sfs-hint">
              <FaLink /> Select a product below to map it to this ASIN
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="sfs-filters">
        <div className="sfs-filter-group">
          <label>SORT BY:</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="relevance">Relevance</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
            <option value="similarity">Similarity</option>
          </select>
        </div>
        <div className="sfs-filter-group">
          <label>PRICE RANGE:</label>
          <select value={priceRange} onChange={(e) => setPriceRange(e.target.value)}>
            <option value="all">All Prices</option>
            <option value="0-50">0 - 50</option>
            <option value="50-100">50 - 100</option>
            <option value="100-200">100 - 200</option>
            <option value="200+">200+</option>
          </select>
        </div>
      </div>

      {/* Results */}
      <div className="sfs-results">
        <div className="sfs-results-header">
          <span>{filteredAndSortedProducts.length} similar products found</span>
          {loading && <span className="sfs-searching"> - Searching...</span>}
        </div>

        {loading && similarProducts.length === 0 && (
          <div className="sfs-loading">
            <FaSpinner className="sfs-spinner" />
            <p>Searching for similar products on BTPL/Global...</p>
          </div>
        )}

        <div className="sfs-grid">
          {filteredAndSortedProducts.map(product => (
            <div key={product.id} className="sfs-card">
              <div className="sfs-match-badge">{product.similarity}% match</div>

              <div
                className="sfs-card-image"
                onClick={() => handleOpenProduct(product)}
                title="Click to open on 1688"
              >
                <img
                  src={product.image}
                  alt={product.title}
                  referrerPolicy="no-referrer"
                  crossOrigin="anonymous"
                  onError={handleImageError}
                />
              </div>

              <div className="sfs-card-body">
                <h4
                  className="sfs-card-title"
                  title={product.title}
                  onClick={() => handleOpenProduct(product)}
                  style={{ cursor: 'pointer' }}
                >
                  {product.title}
                </h4>
                <p className="sfs-card-supplier">{product.supplier}</p>
                <p className="sfs-card-offerid">Offer ID: {product.offerId}</p>

                <div className="sfs-card-prices">
                  <div className="sfs-price-row">
                    <span className="sfs-price-label">INR (Algo):</span>
                    <span className="sfs-price-inr">₹{product.price.inr.toFixed(2)}</span>
                  </div>
                </div>

                {/* Variants Section */}
                {product.variants && product.variants.length > 0 && (
                  <div className="sfs-variants-section">
                    <div className="sfs-variants-title">Variants ({product.variants.length})</div>
                    <div className="sfs-variants-list">
                      {product.variants.map((variant, idx) => (
                        <div key={variant.skuId || idx} className="sfs-variant-item">
                          <span className="sfs-variant-sku" title={variant.attributes}>
                            {variant.skuId} - {variant.attributes}
                          </span>
                          <button
                            className="sfs-variant-map-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMapProduct(product, variant.skuId);
                            }}
                            disabled={mappingProductId === `${product.id}-${variant.skuId}`}
                          >
                            {mappingProductId === `${product.id}-${variant.skuId}` ? '...' : 'Map'}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="sfs-card-actions">
                  <button
                    className="sfs-btn-view"
                    onClick={() => handleOpenProduct(product)}
                  >
                    <FaExternalLinkAlt /> View Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {!loading && filteredAndSortedProducts.length === 0 && (
          <div className="sfs-no-results">
            <p>No similar products found. Try a different image or search manually.</p>
          </div>
        )}
      </div>

      {hasMoreProducts && (
        <div className="sfs-load-more">
          <button
            className="sfs-btn-load-more"
            onClick={loadMoreProducts}
            disabled={loading || loadingMore}
          >
            {loadingMore ? 'Loading More...' : 'Load More Products'}
          </button>
        </div>
      )}
    </div>
  );
};

export default SellerFindSimilar;
